import { useMutation } from '@tanstack/react-query';
import {
  formatMoney,
  fromCents,
  type InvoiceResponse,
  lineSplitError,
  parseDecimal,
  toCents,
} from '@k-management/shared';
import { useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { ReclassPicker } from '../../components/ReclassPicker.js';
import { apiRequest } from '../../lib/api.js';

type Line = InvoiceResponse['lines'][number];

/** What the field holds in the form of the API, or 0.00 when it is not a number. */
const amountOf = (value: string) => fromCents(Math.round((parseDecimal(value) ?? 0) * 100));

/**
 * «Dividi su più voci» (3.19): the amount of a line on more items of the plan
 * Base. The line stays one: only how it is shared out changes.
 */
export function LineSplitDialog({
  invoice,
  line,
  onSaved,
  onClose,
}: {
  invoice: InvoiceResponse;
  line: Line;
  onSaved: (updated: InvoiceResponse) => Promise<void>;
  onClose: () => void;
}) {
  const [parts, setParts] = useState<{ code: string | null; amount: string }[]>(
    line.reclassParts.length > 0
      ? line.reclassParts
      : [
          { code: line.reclassCode ?? invoice.reclass.code, amount: line.amount },
          { code: null, amount: '' },
        ],
  );
  const save = useMutation({
    mutationFn: (body: { code: string; amount: string }[]) =>
      apiRequest<InvoiceResponse>(
        `/treasury/invoices/${invoice.id}/lines/${line.position}/reclass-parts`,
        { method: 'PUT', body: { parts: body } },
      ),
    onSuccess: async (updated) => {
      await onSaved(updated);
      onClose();
    },
  });
  const body = parts.map((part) => ({ code: part.code ?? '', amount: amountOf(part.amount) }));
  const missing = parts.some((part) => !part.code);
  const problem = missing ? 'Scegli la voce di ogni parte' : lineSplitError(line.amount, body);
  const total = body.reduce((sum, part) => sum + toCents(part.amount), 0);
  const change = (index: number, next: Partial<(typeof parts)[number]>) =>
    setParts(parts.map((part, at) => (at === index ? { ...part, ...next } : part)));

  return (
    <ConfirmDialog
      title="Dividi la riga su più voci"
      confirmLabel="Salva"
      wide
      busy={save.isPending}
      disabled={Boolean(problem)}
      error={save.error?.message}
      onConfirm={() => save.mutate(body)}
      onCancel={onClose}
    >
      <p className="small">
        <strong>{line.description.split('\n')[0]}</strong> · {formatMoney(line.amount)}
      </p>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Voce</th>
              <th className="numeric">Importo</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {parts.map((part, index) => (
              <tr key={index}>
                <td className="line-reclass">
                  <ReclassPicker
                    value={part.code}
                    choices={invoice.options.reclass}
                    noneLabel="— scegli"
                    onChange={(code) => change(index, { code })}
                  />
                </td>
                <td className="numeric">
                  <DecimalInput
                    className="amount-input"
                    aria-label={`Importo della voce ${index + 1}`}
                    decimals={[2, 2]}
                    value={part.amount}
                    onChange={(amount) => change(index, { amount })}
                  />
                </td>
                <td>
                  {parts.length > 2 && (
                    <button
                      type="button"
                      className="small"
                      onClick={() => setParts(parts.filter((_, at) => at !== index))}
                    >
                      Togli
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="row between">
        <button type="button" onClick={() => setParts([...parts, { code: null, amount: '' }])}>
          Aggiungi voce
        </button>
        {line.reclassParts.length > 0 && (
          <button type="button" disabled={save.isPending} onClick={() => save.mutate([])}>
            Togli la divisione
          </button>
        )}
      </div>
      <p className={`small ${problem ? 'error' : 'ok'}`}>
        Totale {formatMoney(fromCents(total))}: {problem ?? 'torna con la riga'}.
      </p>
    </ConfirmDialog>
  );
}
